// import CryptoJS from 'crypto-js';
// import { Store } from '../configureStore';

import { getCookie, setCookie } from "../../helper/cookieHelper";
import { ACCESS_TOKEN, SERVERKNOWNSALT } from "../constants";
import { getServerInfo } from "../features/auth/AuthAPI";

// /**
//  * Decodes a JWT access token and extracts the sessionId.
//  * Checks jti → sessionId → sub fields in order.
//  */
// export const extractSessionIdFromToken = (accessToken: string): string => {
//   try {
//     const parts = accessToken.split('.');
//     if (parts.length !== 3) return '';
//     const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
//     const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
//     const payload = JSON.parse(atob(padded));
//     return payload.jti || payload.sessionId || payload.sub || '';
//   } catch {
//     return '';
//   }
// };

// /**
//  * Derives a 256-bit HMAC key from sessionId + serverKnownSalt using PBKDF2-SHA256.
//  * Returns the key as a hex string — store this in Redux (not localStorage/session/cookie).
//  */
// export const deriveHmacKey = (sessionId: string, serverKnownSalt: string): string => {
//   const key = CryptoJS.PBKDF2(sessionId, serverKnownSalt, {
//     keySize: 256 / 32,
//     iterations: 10000,
//     hasher: CryptoJS.algo.SHA256,
//   });
//   return key.toString(CryptoJS.enc.Hex);
// };

// /**
//  * Builds the canonical message string for signing.
//  * Format: METHOD|url|body|timestamp
//  */
// export const buildHmacMessage = (
//   method: string,
//   url: string,
//   body: string,
//   timestamp: string
// ): string => `${method.toUpperCase()}|${url}|${body}|${timestamp}`;

// /**
//  * Signs a message using the hex-encoded HMAC key from Redux store.
//  * Returns base64-encoded HMAC-SHA256 signature.
//  */
// export const signRequest = (message: string, keyHex: string): string => {
//   const key = CryptoJS.enc.Hex.parse(keyHex);
//   return CryptoJS.HmacSHA256(message, key).toString(CryptoJS.enc.Base64);
// };

// /**
//  * Reads the HMAC key from Redux store, signs the request, and returns headers.
//  * Returns empty object if key is not yet derived.
//  */
// export const getHmacHeaders = (
//   method: string,
//   url: string,
//   body: string
// ): Record<string, string> => {
//   const hmacKey = (Store.getState() as any)?.hmac?.hmacKey;
//   if (!hmacKey) return {};
//   const timestamp = Date.now().toString();
//   const message = buildHmacMessage(method, url, body, timestamp);
//   const signature = signRequest(message, hmacKey);
//   return {
//     'X-HMAC-Signature': signature,
//     'X-Request-Timestamp': timestamp,
//   };
// };



const encoder = new TextEncoder();

let hmacKey: CryptoKey | null = null;


export async function deriveHmacKey(
  accessToken: string,
  serverSalt: string,
  sessionId: string
) {
  
  
  // Step 1: Import accessToken as key material
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    encoder.encode(accessToken),
    'HKDF',
    false,
    ['deriveKey']
  );

  // Step 2: Derive HMAC key using HKDF
  hmacKey = await crypto.subtle.deriveKey(
    {
      name: 'HKDF',
      hash: 'SHA-256',
      salt: encoder.encode(serverSalt),
      info: encoder.encode(`hmac-session-key:${sessionId}`), 
    },
    keyMaterial,
    {
      name: 'HMAC',
      hash: 'SHA-256',
      length: 256,
    },
    false,
     ['sign', 'verify']
  );

  return hmacKey;
}
// export function getSessionIdFromToken(token: string): string {
//   const payload =token ? JSON.parse(atob(token?.split('.')[1])):"";
//   return payload.sessionId; // or sub / sid based on your backend
// }
 
export function getSessionIdFromToken(token: string): string {
 
  const payload = token ? JSON.parse(atob(token?.split('.')[1])) : "";
 
  return payload.sid; // ✅ was payload.sessionId — undefined in Keycloak JWTs
 
}

export async function signMessage(message: string) {

if (!hmacKey) {
    throw new Error('HMAC key initialization failed');
  }

  const sigBuffer = await crypto.subtle.sign(
    'HMAC',
    hmacKey,
    new TextEncoder().encode(message)
  );

  return Array.from(new Uint8Array(sigBuffer))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}


export async function createRequest(data: any) {
  const payload = (JSON.stringify(data));
  const timestamp = Date.now().toString();
  const nonce = crypto.randomUUID();

  const message = `${payload.length}:${payload}|${timestamp}|${nonce}`;


  if (!hmacKey) {
    console.log("HMAC key not found, initializing...");
    await initHmac();
  }

  // 🔹 Still not available → throw error
  
  const signature = await signMessage(message);
 

  return {
   
    payload,
    timestamp: Number(timestamp),
    nonce,
    signature
  };
}
const responseNonces = new Set(); 

  

function isNonceSeen(nonce:any) { 

  if (responseNonces.has(nonce)) return true; 

  responseNonces.add(nonce); 

  // Prune old entries if the set grows large 

  if (responseNonces.size > 1000) { 

    const first = responseNonces.values().next().value; 

    responseNonces.delete(first); 

  } 

  return false; 

} 
function hexToBuffer(hex:any) { 

  const bytes = new Uint8Array(hex.length / 2); 

  for (let i = 0; i < hex.length; i += 2) { 

    bytes[i / 2] = parseInt(hex.substring(i, i + 2), 16); 

  } 

  return bytes.buffer; 

} 

export async function validateAndParseResponse(response: any): Promise<any> {
  const { data, dataString,timestamp, nonce, signature } = response;
  
  // Re-initialize key if lost (e.g. page refresh without re-login)
  if (!hmacKey) {
    await initHmac();
  }
  if (!hmacKey) {
    throw new Error("HMAC_KEY_NOT_INITIALIZED");
  }
 
  // Replay protection — reject duplicate nonces
  if (isNonceSeen(nonce)) {
    throw new Error("REPLAY_ATTACK_DETECTED");
  }
  const message = `${dataString.length}:${dataString}|${timestamp}|${nonce}`;
  let isValid: boolean;
  try {
    isValid = await crypto.subtle.verify(
      "HMAC",
      hmacKey,
      new Uint8Array(hexToBuffer(signature)),
      encoder.encode(message)
    );
  } catch (err) {
   
    throw new Error("HMAC_VERIFY_ERROR");
  }
 
 
 
  if (!isValid) {
    throw new Error("INVALID_SIGNATURE");
  }
 

   if (isValid) {
    return {
      status: 200,
      valid: true,
      data: data
    };
  } else {
    return {
      status: 400,
      valid: false,
      message: "Validation failed"
    };
  }
}

export async function initHmac() {
  const accessToken = getCookie(ACCESS_TOKEN);
  if (!accessToken) return;

  let serverKnownSalt = getCookie(SERVERKNOWNSALT);
  const sessionId = getSessionIdFromToken(accessToken);

  try {
    if (!serverKnownSalt) {
      const response = await getServerInfo();
      serverKnownSalt = response?.data?.value;
      if (serverKnownSalt) {
        setCookie(SERVERKNOWNSALT, serverKnownSalt);
      }
    }

    if (serverKnownSalt) {
      await deriveHmacKey(accessToken, serverKnownSalt, sessionId);
    }
  } catch {

  }
}


